import { useMemo } from 'react';
import { pick } from 'lodash/fp';
import {
  LabelOrigin,
  LabelSuggestionDetails,
  LabelValue,
  LabelValueDetails,
  TimeSignal
} from 'graphql/types';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import {
  useIsSmartSuggestionSettingEnabled,
  determineLabelAcceptedBy
} from 'modules/timesheet-labels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';

export const picker = pick(['idValue', 'numericValue', 'stringValue']);

export const useCollatedCustomLabels = (
  timeSignal: TimeSignal,
  labels: CustomLabel[]
): CustomLabel[] => {
  const labelValues = useMemo(
    () => (timeSignal?.labelValues ?? []) as LabelValueDetails[],
    [timeSignal?.labelValues]
  );
  const suggestions = timeSignal?.suggestions
    ?.suggestions as LabelSuggestionDetails[];
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  return useMemo(
    () =>
      (labelValues || [])
        .filter(
          (label) => label.label.origin === LabelOrigin.User && label.value
        )
        .map((labelValue: LabelValueDetails) => {
          const label = (labels ?? []).find(
            (label) => labelValue.label.id == label.id
          );
          const suggestion = suggestions?.find(
            (suggestion: LabelSuggestionDetails) =>
              suggestion?.label?.id == label?.id
          );
          const suggestedValue = isTimeSignalFederatedSuggestionsEnabled
            ? (suggestion?.suggestedValues?.map(picker) as [LabelValue])
            : labelValue?.suggestedValues?.map(picker);
          const suggestionMetadata = isTimeSignalFederatedSuggestionsEnabled
            ? suggestion?.suggestionMetadata
            : labelValue?.suggestionMetadata;
          return {
            ...(label ?? labelValue.label),
            value: labelValue?.value ? picker(labelValue.value) : null,
            valueSource: labelValue.valueSource,
            suggestedValues: suggestedValue ?? undefined,
            suggestionMetadata: suggestionMetadata ?? undefined,
            thresholdMet: suggestion?.thresholdMet ?? false,
            acceptedBy: determineLabelAcceptedBy(
              labelValue.valueSource,
              labelValue.acceptedBy,
              isSmartSuggestionEnabled
            )
          };
        }),
    [
      labelValues,
      labels,
      suggestions,
      isTimeSignalFederatedSuggestionsEnabled,
      isSmartSuggestionEnabled
    ]
  );
};
